import { storeToRefs } from "pinia"
import { useConfigStore } from "@/stores/config"
import Bugsnag from "@bugsnag/js"
import axios, {
  AxiosError,
  type AxiosPromise,
  type AxiosRequestConfig,
} from "axios"
import { formatEndpoint, setDB, getDB } from "@/plugins/mockDataRecorder"
import { getShouldNotifyBugSnag } from "@/helpers/bugsnag"
import { isInIframe } from "@/helpers/isInIframe"
import { useExitIframeModalStore } from "@/components/af/ExitIframeModal/ExitIframeModal.store"

axios.defaults.headers.common = {
  Accept: "application/json",
  "Content-Type": "application/json",
}

// Send a logged out user to the login page matching his organisation.
// ?authRedirect=<slug> in the URL means the user comes from an SSO organisation:
// he must log in on its own SSO page, and forceRedirect brings him back to the app
// afterwards — urls.login already carries its own forceRedirect from the config.
// "af" (like no param at all) means the standard AssessFirst login.
function redirectToLogin() {
  const { urls } = useConfigStore()
  const authRedirect = new URLSearchParams(window.location.search).get(
    "authRedirect"
  )
  const forceRedirect = encodeURIComponent(urls.app)

  document.location.href =
    authRedirect && authRedirect !== "af"
      ? `${urls.sso_login}/${authRedirect}/login?forceRedirect=${forceRedirect}`
      : urls.login
}

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Auth redirect
    // Dans une iframe (intégration ATS), Chrome bloque les cookies tiers : /me
    // répond 401 et la page de login ne peut pas s'afficher dans la frame. On
    // affiche la modale de sortie d'iframe au lieu de rediriger.
    if (error?.response?.status === 401) {
      if (isInIframe()) {
        useExitIframeModalStore().isBlocked = true
      } else {
        redirectToLogin()
      }
      return Promise.reject(error)
    }

    // Rate limiting
    if (error?.response?.status === 429) {
      console.warn("429 Too Many Attempts")
      window.alert("Take your time, the server feels under attack 😅")
    }

    return Promise.reject(error)
  }
)

function makeRequest<T>(options: AxiosRequestConfig): AxiosPromise<T> {
  const { urls } = storeToRefs(useConfigStore())

  const baseUrl = options.baseURL || urls.value.api

  return axios({
    ...options,
    withCredentials: true,
    url: `${baseUrl}${options.url}`,
    validateStatus: (status) => status < 400,
  })
}

export default async function <T>(options: AxiosRequestConfig) {
  const configStore = useConfigStore()
  const { mockMode } = storeToRefs(configStore)

  // Mock reading
  if (mockMode.value === "read") {
    const endpoint = formatEndpoint(options)
    return await getDB(endpoint)
  }

  try {
    const res = await makeRequest<T>(options)

    // Mock recording
    if (mockMode.value === "record") {
      const endpoint = formatEndpoint(options)
      setDB(endpoint, res.data)
    }

    return res.data
  } catch (error) {
    // Check error type
    if (!(error instanceof Error)) {
      throw error
    }

    // Bugsnag notifications
    if (error instanceof AxiosError && error.response) {
      if (getShouldNotifyBugSnag(error.response)) {
        Bugsnag.notify(error, (event) => {
          event.addMetadata("errorResponse", error.response?.data)
          event.addMetadata("request", {
            payload: error.config?.data,
            url: error.config?.url,
            method: error.config?.method,
          })
        })
      }
    }

    throw error
  }
}
